<template>
  <page-section
    :class="`twoLocations ${compact ? 'twoLocations--compact' : ''}`"
    :revealOnScroll="false"
  >
    <template v-if="!compact">
      <div class="map__container" ref="mapContainer">
        <svg
          width="1320"
          height="1227"
          viewBox="0 0 1320 1227"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          class="map__image"
          preserveAspectRatio="xMidYMid meet"
          ref="mapImage"
        >
          <defs>
            <radialGradient
              id="grad1"
              cx="50%"
              cy="50%"
              r="50%"
              fx="50%"
              fy="50%"
            >
              <stop offset="70%" stop-color="white" />
              <stop offset="100%" stop-color="#101010" />
            </radialGradient>
            <radialGradient
              id="grad2"
              cx="50%"
              cy="50%"
              r="50%"
              fx="50%"
              fy="50%"
            >
              <stop offset="20%" stop-color="white" />
              <stop offset="45%" stop-color="#000" />
            </radialGradient>

            <mask id="gradientmask" x="0" y="0" width="100%" height="100%">
              <rect width="100%" height="100%" x="0" y="0" fill="#101010" />
              <circle
                cx="73%"
                cy="86%"
                r="19%"
                fill="url(#grad1)"
                class="circle"
              />
            </mask>
            <mask
              id="gradientmask--more-transparent"
              x="0"
              y="0"
              width="100%"
              height="100%"
            >
              <circle
                cx="73%"
                cy="86%"
                r="100%"
                fill="url(#grad2)"
                class="circle"
              />
            </mask>
          </defs>
          <image
            :href="mapImageSrc"
            width="1320"
            height="1227"
            mask="url(#gradientmask)"
          />
          <path
            d="M951.5 1064L937 1072L928 1048.5V973.5L912 913.5L886 845.5L875.5 814L835 743L776 659.5L697.5 546.5L626.5 450L644 388.5L589 374L471 345L459 328H430.5H404H341L192 269L163 261L171 248"
            stroke="#FFCB40"
            stroke-width="10"
            ref="walkingpathoverlay"
            mask="url(#gradientmask--more-transparent)"
          />
          <circle
            cx="964"
            cy="1057"
            r="10"
            fill="#f26b65"
            class="circle"
            ref="locationMarker"
          />
        </svg>
      </div>

      <div
        class="twoLocations__text twoLocations__text--location1"
        ref="textcontainerLocation1"
      >
        <h3 class="text__title title--small">
          {{ content.locations.location_1.title }}
        </h3>
        <div class="text__text">
          {{ content.locations.location_1.text }}
        </div>

        <div class="text__button">
          <ButtonLink
            :label="content.locations.location_1.link_title"
            :href="content.locations.location_1.link_naar"
            color="black"
          />
        </div>
      </div>

      <div class="twoLocations__image twoLocations__image--location1">
        <img :src="content.locations.location_1.image" />
      </div>
      <div
        class="twoLocations__text twoLocations__text--location2"
        ref="textcontainerLocation2"
      >
        <h3 class="text__title title--small">
          {{ content.locations.location_2.title }}
        </h3>
        <div class="text__text">
          {{ content.locations.location_2.text }}
        </div>

        <div class="text__button">
          <ButtonLink
            :label="content.locations.location_2.link_title"
            :href="content.locations.location_2.link_naar"
            color="black"
          />
        </div>
        <img :src="content.locations.location_2.image" />
      </div>
    </template>
    <div
      class="twoLocations__image twoLocations__image--final"
      ref="finalImageContainer"
    >
      <img :src="mapImageSrc" ref="finalImage" />
    </div>
    <div
      class="twoLocations__text twoLocations__text--final"
      ref="textcontainerFinal"
    >
      <h3 class="text__title title--small">{{ content.afsluiting.title }}</h3>
      <div class="text__text">
        {{ content.afsluiting.text }}
      </div>

      <div class="readfurther">
        <div class="readfurther__text">
          <h4 class="readfurther__title">
            {{ content.afsluiting.title_links_overview }}
          </h4>
          <div class="readfurther__links">
            <a
              class="readfurther__link"
              :href="content.locations.location_1.link_naar"
              >{{ content.locations.location_1.link_title }}
              <Icon class="readfurther__link_icon" type="arrow-right"
            /></a>
            <a
              class="readfurther__link"
              :href="content.locations.location_2.link_naar"
              >{{ content.locations.location_2.link_title }}
              <Icon class="readfurther__link_icon" type="arrow-right"
            /></a>
          </div>
        </div>
      </div>
    </div>
  </page-section>
</template>
<script setup>
import { defineProps, inject, onMounted, onUnmounted, ref } from "vue";
import gsap from "gsap";

import { Flip } from "gsap/Flip";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { DrawSVGPlugin } from "gsap/DrawSVGPlugin";
import { MotionPathPlugin } from "gsap/MotionPathPlugin";

gsap.registerPlugin(DrawSVGPlugin);
gsap.registerPlugin(MotionPathPlugin);
gsap.registerPlugin(ScrollTrigger);
gsap.registerPlugin(Flip);

// eslint-disable-next-line no-unused-vars
const props = defineProps({
  mapImageSrc: {
    type: String,
    required: true,
  },
  compact: {
    type: Boolean,
    default: false,
  },
  content: {
    type: Object,
    required: true,
  },
});

const emitter = inject("emitter");

const textcontainerLocation1 = ref(null);
const textcontainerLocation2 = ref(null);
const textcontainerFinal = ref(null);
const finalImageContainer = ref(null);
const finalImage = ref(null);
const mapContainer = ref(null);
const locationMarker = ref(null);
const walkingpathoverlay = ref(null);

onMounted(() => {
  if (process.env.NODE_ENV === "development") {
    console.warn(
      "LET OP! De masks van de images worden niet geladen ivm CORS op de live server."
    );
  }

  gsap.set(mapContainer.value, { autoAlpha: 0 });

  if (props.compact) {
    const tl = gsap
      .timeline({
        scrollTrigger: {
          trigger: finalImageContainer.value,
          start: "top 80%",
          markers: false,
        },
      })
      .fromTo(
        finalImageContainer.value,
        { autoAlpha: 0, yPercent: 20 },
        { autoAlpha: 1, yPercent: 0, duration: 1 }
      );
  } else {
    // createScrollTriggers();
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore:next-line
    emitter.on("page-transitions-finished", () => {
      createScrollTriggers();
    });
  }
});

let mm;

const createScrollTriggers = () => {
  mm = gsap.matchMedia();
  mm.add("(min-width: 768px)", () => {
    if (textcontainerLocation1.value) {
      gsap.to(locationMarker.value, {
        scale: 1.5,
        duration: 0.7,
        repeat: -1,
        yoyo: true,
        transformOrigin: "center",
      });

      const location1Tl = gsap.timeline({
        scrollTrigger: {
          trigger: textcontainerLocation1.value,
          start: "top 40%",
          end: "bottom 20%",
          pin: true,
          markers: false,
        },
      });

      // const location2Tl = gsap.timeline({
      //   scrollTrigger: {
      //     trigger: textcontainerLocation2.value,
      //     start: "top 20%",
      //     end: "top 0%",
      //     pin: true,
      //     markers: false,
      //   },
      // });

      const startWalkingPercentage = 20;
      const remainingWalkingPercentage = 100 - startWalkingPercentage;
      const walkingEase = "power1.inOut";

      const backgroundMapTl = gsap
        .timeline({
          scrollTrigger: {
            trigger: textcontainerLocation1.value,
            start: "top 40%",
            end: "bottom -50%",
            scrub: 1,
            markers: false,

            onEnter: () => {
              gsap.to(mapContainer.value, { autoAlpha: 1, overwrite: true });
            },
            onLeaveBack: () => {
              gsap.to(mapContainer.value, { autoAlpha: 0, overwrite: true });
            },
          },
        })
        .to(
          "#gradientmask circle",
          {
            duration: remainingWalkingPercentage / 3,
            ease: walkingEase,
            attr: { r: "150%" },
          },
          startWalkingPercentage
        )
        .to("#gradientmask circle", {
          duration: remainingWalkingPercentage / 3,
          ease: walkingEase,
          attr: { r: "19%" },
        })

        .fromTo(
          walkingpathoverlay.value,
          { drawSVG: "0%" },
          {
            drawSVG: "100%",
            duration: remainingWalkingPercentage,
            ease: walkingEase,
          },
          startWalkingPercentage
        )
        .to(
          locationMarker.value,
          {
            duration: remainingWalkingPercentage,
            ease: "power1.inOut",
            motionPath: {
              path: walkingpathoverlay.value,
              align: walkingpathoverlay.value,
              alignOrigin: [0.5, 0.5],
              autoRotate: false,
            },
          },
          startWalkingPercentage
        )
        .to(
          "mask circle",
          {
            x: "-=800",
            y: "-=800",
            duration: remainingWalkingPercentage,
            ease: walkingEase,
          },
          startWalkingPercentage / 3
        )
        .add(() => {
          //
          // !!! EMPTY !!!
          //
          // timing is set to 100 so we could read timing as "percentage of complete timeline"
          // (only works as other animations don't overgrow this time)
          //
          // !!! EMPTY !!!
          //
        }, 100);

      // END PRESENTATION
      const backgroundMapTl2 = gsap.timeline({
        scrollTrigger: {
          trigger: textcontainerFinal.value,
          start: "top 90%",
          markers: false,
          onEnter: () => {
            const flipState = Flip.getState(mapContainer.value);
            Flip.fit(mapContainer.value, finalImage.value, {
              absolute: true,
            });
            Flip.from(flipState, { duration: 1, ease: "power3.out" })
              .to(
                "#gradientmask circle",
                {
                  duration: 0.5,
                  ease: "power1.inOut",
                  attr: { r: "150%" },
                },
                0
              )
              .to(
                "#gradientmask--more-transparent circle",
                {
                  duration: 0.5,
                  ease: "power1.inOut",
                  attr: { r: "300%" },
                },
                0
              );
          },
          onLeaveBack: () => {
            const flipState = Flip.getState(mapContainer.value);
            gsap.set(mapContainer.value, { clearProps: "all" });
            Flip.from(flipState, { duration: 1, ease: "power3.out" })
              .to(
                "#gradientmask circle",
                {
                  duration: 0.2,
                  ease: "power1.inOut",
                  attr: { r: "19%" },
                },
                0
              )
              .to(
                "#gradientmask--more-transparent circle",
                {
                  duration: 0.2,
                  ease: "power1.inOut",
                  attr: { r: "100%" },
                },
                0
              );
          },
        },
      });
    }
  });
};
onUnmounted(() => {
  if (mm) mm.revert(); // verwijdert alle mm.add() zaken
});
</script>

<style lang="scss" scoped>
.twoLocations {
  $marginBeforeSecondRow: 40vh;

  &__text {
    margin-bottom: 1rem; // distance between sections
    @include mq(small) {
      min-height: 30vh; // defines scroll speed!!
    }
    &--location1 {
      @include mq(small) {
        grid-column: 6 / span 14;
        grid-row: 1/2;
        min-height: 50vh; // defines scroll speed!!
      }
    }
    &--location2 {
      @include mq(small) {
        margin-bottom: 20vh;
        margin-top: $marginBeforeSecondRow;
        grid-column: 22/-6;
        grid-row: 2/3;
      }
      img {
        mask-image: url("https://www.nederlandsmijnmuseum.nl/assets/masks/coal2.svg");
        mask-size: 100%;
        mask-repeat: no-repeat;
        mask-position: center;
        position: relative;
        width: 70%;
        margin-left: 30%;
        @include mq(small) {
          top: 4rem;
          left: -10rem;
          width: 100%;
          margin-left: 0;
        }
      }
    }
    &--final {
      @include mq(small) {
        grid-column: 5 / span 15;
        grid-row: 3/4;
        margin-top: 8rem;
      }
      min-height: 0; // defines scroll speed!!
    }
  }
  &__image {
    position: relative;
    img {
      mask-size: 100%;
      mask-repeat: no-repeat;
      mask-position: center;
      @include mq(small) {
        transform: translateY(-20%);
      }
    }
    &--location1 {
      @include mq(small) {
        grid-column: 22/-6;
        grid-row: 1/2;
      }
      img {
        mask-image: url("https://www.nederlandsmijnmuseum.nl/assets/masks/coal3.svg");
        width: 70%;
        @include mq(small) {
          width: 100%;
        }
      }
    }
    &--location2 {
      /* NOT IN USE ANYMORE? */
      margin-top: $marginBeforeSecondRow;
      grid-column: 6 / span 14;
      grid-row: 2/3;
      img {
        mask-image: url("https://www.nederlandsmijnmuseum.nl/assets/masks/coal2.svg");
      }
    }
    &--final {
      grid-column: 13 / span 24;

      grid-row: 3/4;
      img {
        @include mq(small) {
          opacity: 0;
        }
        // position: relative;
        // border: 1px solid red;
      }
    }
  }

  &--compact {
    .twoLocations {
      &__text {
        &--final {
          grid-column: 5 / span 15;
          grid-row: 1 / 2;
          margin-top: 8rem;
        }
      }
      &__image {
        &--final {
          grid-column: 13 / span 24;
          grid-row: 1 / 2;
          img {
            opacity: 1;
          }
        }
      }
    }
  }
}
.map {
  &__container {
    display: none;
    @include mq(small) {
      display: block;
      position: fixed;
      z-index: -10;
      top: 5%;
      height: 90%;
      left: 5%;
      width: 90%;
    }
    width: 100%;
  }
  &__image {
    height: 100%;
    display: block;
    margin: 0 auto;
  }
}

/* below comming from ContentblockText.scss */
.text {
  &__title {
    margin: 0 0 1.2rem 0;
    &--small {
      width: 75%;
      margin: 0 0 1em 0;
    }
  }
  &__text {
    margin-bottom: 1em;

    :deep(li) {
      margin-top: -1em; //wordpress add BR before LI
    }
  }
  &__with_subject {
    margin: 3em 0;
    @include mq(small) {
      display: flex;
      justify-content: space-between;
    }

    &__subject {
      @include mq(small) {
        position: absolute;
        width: calc($column-size * 5);
        right: 0;
        top: 0;
        text-align: right;
      }

      &_container {
        position: relative;
        @include niveau-grotesk-medium;
        @include mq(small) {
          width: calc($column-size * 3);
        }
      }
    }
    &__text {
      @include mq(small) {
        width: calc($column-size * 20);
      }
    }
  }
  &__button {
    display: block;
  }
  &__introduction {
    @include typo--content-introduction;
    margin-bottom: 1rem;
  }
}

/* below comming from ContentblockReadFurther.scss */
.readfurther {
  &__title {
    font-size: 1rem;
  }
  &__link {
    display: block;
    // border-bottom: 1px solid colors("grey");
    font-size: 0.95rem;
    padding: 0.2em 0;
    text-decoration: none;
    color: colors("blackish");
    display: flex;
    align-items: center;
    :deep(.readfurther__link_icon) {
      margin-left: 0.5em;
      transition: 0.2s;
    }
    &:last-of-type {
      border-bottom: none;
    }
    &:hover {
      :deep(.readfurther__link_icon) {
        transform: translateX(0.5em);
      }
    }
  }
}
</style>
